<script setup>
import { ref, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import SourcesAPI from 'dashboard/api/ingestionSources';

const sources = ref([]);
const loading = ref(false);
const newName = ref('');
const newKind = ref('generic');
const revealedToken = ref(null);

const load = async () => {
  loading.value = true;
  try {
    const { data } = await SourcesAPI.get();
    sources.value = data || [];
  } catch (e) {
    useAlert('Не удалось загрузить источники');
  } finally {
    loading.value = false;
  }
};
onMounted(load);

const create = async () => {
  if (!newName.value.trim()) return;
  try {
    const { data } = await SourcesAPI.create({
      name: newName.value.trim(),
      kind: newKind.value,
    });
    revealedToken.value = { name: data.name, token: data.token };
    newName.value = '';
    await load();
  } catch (e) {
    useAlert('Не удалось создать источник (имя уже занято?)');
  }
};

const rotate = async id => {
  if (!window.confirm('Перевыпустить токен? Старый перестанет работать.')) return;
  try {
    const { data } = await SourcesAPI.rotateToken(id);
    revealedToken.value = { name: data.name, token: data.token };
    await load();
  } catch (e) {
    useAlert('Не удалось перевыпустить токен');
  }
};

const remove = async id => {
  if (!window.confirm('Удалить источник?')) return;
  try {
    await SourcesAPI.delete(id);
    await load();
  } catch (e) {
    useAlert('Не удалось удалить источник');
  }
};
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-surface-1">
    <div class="w-full px-6 py-6">
      <h1 class="mb-1 text-xl font-medium text-n-slate-12">Источники данных</h1>
      <p class="mb-4 text-sm text-n-slate-11">
        Токены коннектора (Ingestion API). Фидер (поллер MedUp MRES, вебхуки Bitrix,
        скрипты) шлёт операции и профили с этим Bearer-токеном.
      </p>

      <div
        v-if="revealedToken"
        class="p-3 mb-4 text-sm border rounded-lg border-n-amber-6 bg-n-amber-2 text-n-slate-12"
      >
        Токен для «{{ revealedToken.name }}» (показывается один раз):
        <code class="font-mono break-all">{{ revealedToken.token }}</code>
      </div>

      <div class="flex items-end gap-2 mb-6">
        <label class="flex flex-col text-xs text-n-slate-11">
          Имя источника
          <input
            v-model="newName"
            class="px-2 py-1.5 mt-1 text-sm border rounded-lg border-n-weak bg-n-solid-1 text-n-slate-12"
            placeholder="MedUp MRES feeder"
          />
        </label>
        <label class="flex flex-col text-xs text-n-slate-11">
          Тип
          <select
            v-model="newKind"
            class="px-2 py-1.5 mt-1 text-sm border rounded-lg border-n-weak bg-n-solid-1 text-n-slate-12"
          >
            <option value="generic">generic</option>
            <option value="mres">mres</option>
            <option value="bitrix">bitrix</option>
          </select>
        </label>
        <button
          type="button"
          class="px-3 py-1.5 text-sm text-white rounded-lg bg-n-brand"
          @click="create"
        >
          Создать
        </button>
      </div>

      <div v-if="loading" class="py-10 text-center text-n-slate-11">Загрузка…</div>
      <div
        v-else-if="!sources.length"
        class="py-10 text-center text-n-slate-11"
      >
        Источников пока нет.
      </div>
      <div v-else class="overflow-x-auto border rounded-xl border-n-weak bg-n-solid-1">
        <table class="w-full text-sm border-collapse">
          <thead>
            <tr class="border-b border-n-weak">
              <th class="px-3 py-2 font-medium text-left text-n-slate-11">Имя</th>
              <th class="px-3 py-2 font-medium text-left text-n-slate-11">Тип</th>
              <th class="px-3 py-2 font-medium text-left text-n-slate-11">Токен</th>
              <th class="px-3 py-2 font-medium text-left text-n-slate-11">Использован</th>
              <th class="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="s in sources"
              :key="s.id"
              class="border-b border-n-weak last:border-0"
            >
              <td class="px-3 py-2 font-medium text-n-slate-12">{{ s.name }}</td>
              <td class="px-3 py-2 text-n-slate-11">{{ s.kind }}</td>
              <td class="px-3 py-2 font-mono text-xs text-n-slate-11">{{ s.token }}</td>
              <td class="px-3 py-2 text-n-slate-11">{{ s.last_used_at || '—' }}</td>
              <td class="px-3 py-2 text-right whitespace-nowrap">
                <button
                  class="px-2 py-1 text-xs border rounded border-n-weak text-n-slate-11"
                  @click="rotate(s.id)"
                >
                  Перевыпустить
                </button>
                <button
                  class="px-2 py-1 ml-1 text-xs border rounded border-n-weak text-n-ruby-11"
                  @click="remove(s.id)"
                >
                  Удалить
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
